import { Language } from '@app/lib/store-context/Context';
import { getAllEnvironmentContexts } from '@app/lib/store-context/get-all-environment-contexts';
import { getContext } from '@app/lib/store-context/get-context';

// Hosts that belong to the storefront itself. Only absolute URLs pointing at
// these hosts get a locale prefix applied to their pathname. Third-party links
// (e.g. strava.app.link) and non-storefront maap.cc subdomains
// (support.maap.cc, my.maap.cc, the Shopify branded domains, etc.) must be
// left untouched.
const INTERNAL_HOSTS: ReadonlySet<string> = new Set(['maap.cc', 'www.maap.cc']);

function isInternalHost(hostname: string): boolean {
    return INTERNAL_HOSTS.has(hostname.toLowerCase());
}

function removeLocaleFromPath(path: string): string {
    const contexts = getAllEnvironmentContexts();
    for (const context of contexts) {
        path = path
            .replace(`/${context.countryCode.toLowerCase()}/`, '/')
            .replace(new RegExp(`/${context.countryCode.toLowerCase()}$`), '')
            .replace(`/${context.countryCode}`, '')
            .replace(`/${context.language}`, '');
    }
    return path;
}

function localiseSinglePath(cleanPath: string, lang: Language): string {
    if (lang === 'en-AU') {
        return cleanPath;
    }
    const context = getContext(lang);
    if (cleanPath.slice(0, 1) === '/') {
        return `/${context.countryCode.toLowerCase()}${cleanPath}`;
    }
    return `/${context.countryCode.toLowerCase()}/${cleanPath}`;
}

export default function localisePath(
    targetPath: string,
    lang: Language
): string {
    if (!lang) {
        console.warn(
            'Localise path function/Linker component warning.... No lang provided!'
        );
    }

    if (!targetPath) {
        return '';
    }

    // Absolute URL — only localise the pathname when it points at the
    // storefront's own host. External links (and non-storefront maap.cc
    // subdomains) are returned untouched.
    try {
        const parsed = new URL(targetPath);
        if (!isInternalHost(parsed.hostname)) {
            return targetPath;
        }
        const cleanPath = removeLocaleFromPath(parsed.pathname);
        parsed.pathname = localiseSinglePath(cleanPath, lang);
        return parsed.toString();
    } catch {
        // Not a valid absolute URL — treat as a relative path.
    }

    const cleanPath = removeLocaleFromPath(targetPath);
    return localiseSinglePath(cleanPath, lang);
}
